import React, { ReactNode } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { CalenderIcon, ThreeDots } from "../../../public/assets";
import { formatDateTime } from "../../utils/formatTimeAndDate";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/rootReducer";
import {
  setFirstModalOpen,
  setPaymentReceiptModalOpen,
} from "../../store/accountsModal";
import { widths } from "../../template/TransactionTable/data";
import { DescriptionStyle } from "../../template/Receipt/PaymentReceipt";
import { formatAmount } from "../../utils/formatAmount";
import { TransferDetails } from "../../template/TransactionTable";

export interface RowData {
  date: string;
  description: string;
  amount: number;
  paymentType: string;
  transactionId: string;
  status: string;
  transferFee: string;
  recipientTag: string;
  paymentChannel?: string;
  [key: string]: any;
}

interface AccountTransactionTableBodyProps {
  rowObj: RowData;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const AccountTransactionTableBody: React.FC<
  AccountTransactionTableBodyProps
> = ({ rowObj }) => {
  const renderCellData = (data: RowData, column: string): ReactNode => {
    switch (column) {
      case "date":
        return (
          <div style={{ display: "flex", gap: "1rem" }}>
            <img
              src="/assets/svg/calendar.svg"
              alt="calender icon"
              style={{ width: "1.2rem", height: "1.2rem" }}
            />
            <span
              style={{ display: "flex", gap: "0.5rem", whiteSpace: "nowrap" }}
            >
              <strong>{formatDateTime(data.date).date}</strong>
              <span style={{ color: "#868686" }}>
                {formatDateTime(data.date).time}
              </span>
            </span>
          </div>
        );
      case "amount":
        return <span>{formatAmount(data.amount)}</span>;

      case "description":
        return <span>{data.description}</span>;

      case "paymentType":
        return <span>{data.paymentType}</span>;

      case "transactionId":
        return <span>{data.transactionId}</span>;

      case "status":
        return (
          <DescriptionStyle
            as="span"
            $successful={data.status === "complete"}
            $pending={data.status === "pendingApproval"}
            $hasStatus
          >
            {data.status === "complete"
              ? "Successful"
              : data.status === "pendingApproval"
                ? "Pending"
                : "Failed"}
          </DescriptionStyle>
        );
      default:
    }
  };

  const dispatch = useDispatch();

  const handleOpenPaymentReceipt = (receiptObj: RowData) => {
    const obj: TransferDetails = {
      createdAt: receiptObj.date,
      amount: receiptObj.amount,
      transferType: receiptObj.paymentType,
      id: receiptObj.transactionId,
      purpose: receiptObj.description,
      recipientTag: receiptObj.recipientTag,
      transferStatus: receiptObj.status,
      transferFee: receiptObj.transferFee,
      paymentChannel: receiptObj.paymentChannel,
      recipientAccountNumber: receiptObj.recipientAccountNumber,
      recipientBankName: receiptObj.recipientBankName,
      recipientPhoneNumber: receiptObj.recipientPhoneNumber,
      recipientFullName: receiptObj.recipientFullName,
    };
    let modalType = "";
    switch (receiptObj.paymentType) {
      case "fipay":
        modalType = "Fipay Tag";
        break;
      case "bankTransfer":
        modalType = "Bank Transfer";
        break;
      case "wireTransfer":
        modalType = "Wire Transfer";
        break;
      case "payAnyone":
        modalType = "Pay Anyone";
        break;
      default:
        modalType = "default";
    }
    dispatch(
      setPaymentReceiptModalOpen({ open: true, receiptObj: obj, modalType })
    );
    console.log("receiptObj", receiptObj);
  };

  return (
    <div>
      <StyledTableRow
        key={rowObj.transactionId}
        sx={{ width: "100%", display: "flex" }}
      >
        {Object.keys(rowObj)
          .filter((item, index) => item && index <= 5)
          .map((column, index) => (
            <StyledTableCell
              key={column}
              sx={{
                width: widths[index],
              }}
            >
              {renderCellData(rowObj, column)}
            </StyledTableCell>
          ))}
        <StyledTableCell sx={{ width: "7.1%" }}>
          <img
            src="/assets/svg/icon.svg"
            alt="three vertical dots"
            onClick={() => handleOpenPaymentReceipt(rowObj)}
          />
        </StyledTableCell>
      </StyledTableRow>
    </div>
  );
};

export default AccountTransactionTableBody;
